import { translate } from "@atoms/atom-client/i18n";
import type { AngleSectionType } from "../interfaces/AngleSectionType";
import type { CornerBraceType } from "../interfaces/CornerBrace";
import type { CornerType } from "../interfaces/CornerType";
import type { DishType } from "../interfaces/DishType";
import type { FootType } from "../interfaces/FootType";
import type { HandleType } from "../interfaces/HandleType";
import type { HingeType } from "../interfaces/HingeType";
import type { LockType } from "../interfaces/LockType";
import type { PaddingType } from "../interfaces/Padding";
import type { PanelColor } from "../interfaces/PanelColor";
import type { PanelStrength } from "../interfaces/PanelStrength";
import type { PianoHingeType } from "../interfaces/PianoHingeType";
import type { ProfileType } from "../interfaces/ProfileType";
import type { RackRailType } from "../interfaces/RackRailType";
import type { RollerType } from "../interfaces/RollerBoardProperties";
import type { RunnerType } from "../interfaces/RunnerType";
import type { TrayMaterialType } from "../interfaces/TrayMaterialType";
import type { TrolleyHandleType } from "../interfaces/TrolleyHandleType";

export enum LTCaseUserOption {
  AngleSectionType = "AngleSectionType",
  CornerType = "CornerType",
  CornerBraceType = "CornerBraceType",
  DishType = "DishType",
  FootType = "FootType",
  HandleType = "HandleType",
  HingeType = "HingeType",
  LockType = "LockType",
  PianoHingeType = "PianoHingeType",
  PaddingType = "PaddingType",
  PanelColor = "PanelColor",
  PanelStrength = "PanelStrength",
  ProfileType = "ProfileType",
  RackRailType = "RackTailType",
  RollerType = "RollerType",
  RunnerType = "RunnerType",
  TrayMaterialType = "TrayMaterial",
  TrolleyHandleType = "TrolleyHandleType",
}

export type LTCaseUserOptionTypeMap = {
  [LTCaseUserOption.PanelColor]: PanelColor;
  [LTCaseUserOption.ProfileType]: ProfileType;
  [LTCaseUserOption.CornerType]: CornerType;
  [LTCaseUserOption.CornerBraceType]: CornerBraceType;
  [LTCaseUserOption.RollerType]: RollerType;
  [LTCaseUserOption.AngleSectionType]: AngleSectionType;
  [LTCaseUserOption.PanelStrength]: PanelStrength;
  [LTCaseUserOption.DishType]: DishType;
  [LTCaseUserOption.FootType]: FootType;
  [LTCaseUserOption.HandleType]: HandleType;
  [LTCaseUserOption.HingeType]: HingeType;
  [LTCaseUserOption.LockType]: LockType;
  [LTCaseUserOption.PianoHingeType]: PianoHingeType;
  [LTCaseUserOption.PaddingType]: PaddingType;
  [LTCaseUserOption.RackRailType]: RackRailType;
  [LTCaseUserOption.RunnerType]: RunnerType;
  [LTCaseUserOption.TrayMaterialType]: TrayMaterialType;
  [LTCaseUserOption.TrolleyHandleType]: TrolleyHandleType;
};

/** Option kind names in the UI language (read on access). */
export const LTCaseUserOptionTranslationMap: Record<LTCaseUserOption, string> =
  {
    get [LTCaseUserOption.PanelColor]() {
      return translate(
        "app.manufacturer.userOptionKinds.panelColor",
        "Holzfarben",
      );
    },
    get [LTCaseUserOption.ProfileType]() {
      return translate(
        "app.manufacturer.userOptionKinds.profileType",
        "Schlieẞprofile",
      );
    },
    get [LTCaseUserOption.CornerType]() {
      return translate("app.manufacturer.userOptionKinds.cornerType", "Ecken");
    },
    get [LTCaseUserOption.CornerBraceType]() {
      return translate(
        "app.manufacturer.userOptionKinds.cornerBraceType",
        "L-Ecken",
      );
    },
    get [LTCaseUserOption.RollerType]() {
      return translate("app.manufacturer.userOptionKinds.rollerType", "Rollen");
    },
    get [LTCaseUserOption.AngleSectionType]() {
      return translate(
        "app.manufacturer.userOptionKinds.angleSectionType",
        "Kantenschutz",
      );
    },
    get [LTCaseUserOption.PanelStrength]() {
      return translate(
        "app.manufacturer.userOptionKinds.panelStrength",
        "Holzstärken",
      );
    },
    get [LTCaseUserOption.DishType]() {
      return translate(
        "app.manufacturer.userOptionKinds.dishType",
        "Plaketten",
      );
    },
    get [LTCaseUserOption.FootType]() {
      return translate("app.manufacturer.userOptionKinds.footType", "Füẞe");
    },
    get [LTCaseUserOption.HandleType]() {
      return translate("app.manufacturer.userOptionKinds.handleType", "Griffe");
    },
    get [LTCaseUserOption.HingeType]() {
      return translate(
        "app.manufacturer.userOptionKinds.hingeType",
        "Scharniere",
      );
    },
    get [LTCaseUserOption.LockType]() {
      return translate(
        "app.manufacturer.userOptionKinds.lockType",
        "Schlösser",
      );
    },
    get [LTCaseUserOption.PianoHingeType]() {
      return translate(
        "app.manufacturer.userOptionKinds.pianoHingeType",
        "Klavierband",
      );
    },
    get [LTCaseUserOption.PaddingType]() {
      return translate(
        "app.manufacturer.userOptionKinds.paddingType",
        "Schaumstoffe",
      );
    },
    get [LTCaseUserOption.RackRailType]() {
      return translate(
        "app.manufacturer.userOptionKinds.rackRailType",
        "Rackschienen",
      );
    },
    get [LTCaseUserOption.RunnerType]() {
      return translate(
        "app.manufacturer.userOptionKinds.runnerType",
        "Rutschleisten",
      );
    },
    get [LTCaseUserOption.TrayMaterialType]() {
      return translate(
        "app.manufacturer.userOptionKinds.trayMaterialType",
        "Trennbretter",
      );
    },
    get [LTCaseUserOption.TrolleyHandleType]() {
      return translate(
        "app.manufacturer.userOptionKinds.trolleyHandleType",
        "Ausziehgriffe",
      );
    },
  };
